import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, Users, FileText,
  Search, Download, Plus, Trash2, LogOut, ChevronDown,
  FileUp, BarChart3, Clock, CheckCircle2, Send, Loader2,
  AlertTriangle
} from "lucide-react";
import { adminAPI, templateAPI } from "@/lib/api";
import { generateAgreementPDF } from "@/lib/pdfGenerator";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_venue-onboard/artifacts/der85h7h_WhatsApp%20Image%202026-04-24%20at%2011.33.28.jpeg";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  submitted: "bg-amber-50 text-amber-700 border-amber-200",
  executed: "bg-green-50 text-green-700 border-green-200",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("bmm_user") || "{}");
  const [activeTab, setActiveTab] = useState("overview");

  // Dashboard stats
  const [stats, setStats] = useState({ total: 0, draft: 0, submitted: 0, executed: 0 });
  const [venues, setVenues] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [venuesLoading, setVenuesLoading] = useState(false);

  // Template
  const [templateContent, setTemplateContent] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);

  // Sub-admins
  const [subAdmins, setSubAdmins] = useState([]);
  const [showSubAdminDialog, setShowSubAdminDialog] = useState(false);
  const [newSubAdmin, setNewSubAdmin] = useState({ name: "", phone: "", email: "" });
  const [subAdminLoading, setSubAdminLoading] = useState(false);

  // Delete all data state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminAPI.dashboard();
      setStats(res.data.stats);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
  }, []);

  const fetchVenues = useCallback(async () => {
    setVenuesLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await adminAPI.venues(params);
      setVenues(res.data.venues);
    } catch (err) {
      console.error("Venues fetch error:", err);
    } finally {
      setVenuesLoading(false);
    }
  }, [searchQuery, statusFilter]);

  const fetchTemplate = useCallback(async () => {
    try {
      const res = await templateAPI.get();
      setTemplateContent(res.data.template.content);
    } catch (err) {
      // No template
    }
  }, []);

  const fetchSubAdmins = useCallback(async () => {
    try {
      const res = await adminAPI.listSubAdmins();
      setSubAdmins(res.data.subadmins);
    } catch (err) {
      console.error("Sub-admins fetch error:", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchVenues();
    fetchTemplate();
    fetchSubAdmins();
  }, [fetchDashboard, fetchVenues, fetchTemplate, fetchSubAdmins]);

  useEffect(() => {
    fetchVenues();
  }, [searchQuery, statusFilter, fetchVenues]);

  const handleUpdateTemplate = async () => {
    if (!templateContent.trim()) {
      toast.error("Template content cannot be empty");
      return;
    }
    setTemplateLoading(true);
    try {
      await adminAPI.updateTemplate(templateContent);
      toast.success("Agreement template updated!");
    } catch (err) {
      toast.error("Failed to update template");
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'docx') {
      // Send to backend for parsing
      setTemplateLoading(true);
      try {
        const res = await templateAPI.upload(file);
        setTemplateContent(res.data.content);
        toast.success("File parsed. Review and click 'Save Template' to apply.");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to parse .docx file");
      } finally {
        setTemplateLoading(false);
      }
    } else if (ext === 'txt') {
      const reader = new FileReader();
      reader.onload = (evt) => {
        setTemplateContent(evt.target.result);
        toast.success("File loaded. Click 'Save Template' to apply.");
      };
      reader.readAsText(file);
    } else {
      toast.error("Unsupported file type. Please upload .docx or .txt");
    }
    e.target.value = '';
  };

  const handleCreateSubAdmin = async () => {
    if (!newSubAdmin.name || !newSubAdmin.phone) {
      toast.error("Name and Phone are required");
      return;
    }
    setSubAdminLoading(true);
    try {
      await adminAPI.createSubAdmin(newSubAdmin);
      setShowSubAdminDialog(false);
      setNewSubAdmin({ name: "", phone: "", email: "" });
      fetchSubAdmins();
      toast.success("Sub-admin created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create sub-admin");
    } finally {
      setSubAdminLoading(false);
    }
  };

  const handleDeleteSubAdmin = async (id) => {
    try {
      await adminAPI.deleteSubAdmin(id);
      fetchSubAdmins();
      toast.success("Sub-admin removed");
    } catch (err) {
      toast.error("Failed to delete sub-admin");
    }
  };

  const handleDeleteVenue = async (venue) => {
    if (!window.confirm(`Delete venue "${venue.legal_name || venue.phone}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteVenue(venue.id);
      fetchVenues();
      fetchDashboard();
      toast.success("Venue deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete venue");
    }
  };

  const handleDownloadVenuePDF = async (venue) => {
    let agreementText = templateContent;
    if (!agreementText) {
      try {
        const res = await templateAPI.get();
        agreementText = res.data.template.content;
      } catch { agreementText = "Agreement template not available."; }
    }

    const execDate = venue.executed_at
      ? new Date(venue.executed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })
      : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });

    const execDateObj = venue.executed_at ? new Date(venue.executed_at) : new Date();
    const dayNum = execDateObj.getDate();
    const monthName = execDateObj.toLocaleDateString("en-IN", { month: "long" });

    // Determine business type from GSTIN 6th character
    let businessType = "Proprietorship";
    if (venue.gstin && venue.gstin.length >= 6) {
      const sixthChar = venue.gstin.charAt(5).toUpperCase();
      if (sixthChar === "C") {
        businessType = "Company";
      } else if (sixthChar === "F") {
        businessType = (venue.legal_name || "").toUpperCase().includes("LLP") ? "LLP" : "Partnership";
      }
    }

    const venueAddr = venue.venue_address || venue.business_address || "[Venue Address]";
    const signatoryInfo = [venue.signatory_name, venue.signatory_designation].filter(Boolean).join(", ");

    const filledText = agreementText
      // New template placeholders (Ver Final)
      .replace(/\[FULL LEGAL NAME OF VENUE PARTNER\]/g, venue.legal_name || "[FULL LEGAL NAME OF VENUE PARTNER]")
      .replace(/\[Company \/ LLP \/ Partnership Firm \/ Proprietorship Concern\]/gi, businessType)
      .replace(/\[full address\]/g, venue.business_address || "[full address]")
      .replace(/\[name and designation\]/g, signatoryInfo || "[name and designation]")
      .replace(/\[VENUE PARTNER NAME\]/g, venue.legal_name || "[VENUE PARTNER NAME]")
      // Old template placeholders (backward compat)
      .replace(/\[Venue Partner Name\]/g, venue.legal_name || "[Venue Partner Name]")
      .replace(/\[address\]/g, venue.business_address || "[address]")
      .replace(/\[Venue Name\]/g, venue.venue_name || venue.trade_name || venue.legal_name || "[Venue Name]")
      .replace(/\[Venue Address\]/g, venueAddr)
      .replace(/\[Effective Date\]/g, execDate)
      .replace(/\[\s*\]\s*day of\s*\[\s*\]/g, `${dayNum} day of ${monthName}`)
      .replace(/\[●\]/g, execDate)
      .replace(/\[proprietorship \/ partnership \/ company\]/gi, businessType)
      .replace(/\(the address specified herein\)/gi, venueAddr)
      .replace(/\[Venue Partner \/Authorised Signatory Name\]/g, venue.signatory_name || "[Authorised Signatory Name]")
      // Schedule A
      .replace(/Venue Name\nFull Address\nCapacity\nFacilities/g,
        `Venue Name: ${venue.venue_name || venue.trade_name || venue.legal_name || ""}\nFull Address: ${venueAddr}\nCapacity: ${venue.capacity || "N/A"}\nFacilities: ${venue.additional_facilities || "N/A"}`)
      // Skynor signature block (new format)
      .replace(/FOR SKYNOR VENTURES PRIVATE LIMITED[\s\S]*?Date:\s*_+/, (match) =>
        match
          .replace(/Authorised Signatory:\s*_+/, "Authorised Signatory: s/d")
          .replace(/Signature:\s*_+/, "Signature: s/d")
          .replace(/Date:\s*_+/, `Date: ${execDate}`)
      )
      // Old-style Skynor block
      .replace(/For Skynor Ventures Private Limited[\s\S]*?Date:\s*_+/, (match) =>
        match
          .replace(/Signature:\s*_+/, "Signature: s/d")
          .replace(/Name:\s*_+/, "Name: MANIKANDAN A V ACA")
          .replace(/Title:\s*_+/, "Title: CHIEF FINANCIAL OFFICER")
          .replace(/Date:\s*_+/, `Date: ${execDate}`)
      )
      // Remove Venue Partner signature block
      .replace(/FOR\s+(?:\[VENUE PARTNER NAME\]|.{1,80})\n\s*Venue Partner[\s\S]*?Date:\s*_+/g, "");

    try {
      const doc = await generateAgreementPDF({
        venueData: {
          legal_name: venue.legal_name,
          trade_name: venue.trade_name,
          business_address: venue.business_address,
          venue_name: venue.venue_name,
          venue_address: venue.venue_address,
          capacity: venue.capacity,
          additional_facilities: venue.additional_facilities,
          signatory_name: venue.signatory_name,
          signatory_designation: venue.signatory_designation,
          signatory_email: venue.signatory_email,
          signatory_phone: venue.signatory_phone || venue.phone,
        },
        gstin: venue.gstin,
        filledAgreementText: filledText,
        executionTimestamp: venue.executed_at,
      });
      doc.save(`Agreement_${venue.legal_name || venue.phone}.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    }
  };

  const handleDeleteAllData = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeleteLoading(true);
    try {
      await adminAPI.clearData();
      setShowDeleteDialog(false);
      setDeleteConfirmText("");
      fetchDashboard();
      fetchVenues();
      toast.success("All venue and OTP data has been cleared.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear data");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("bmm_token");
    localStorage.removeItem("bmm_user");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top bar - Light theme matching user panel */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BookMyMandap" className="h-9 object-contain" />
            <div className="h-6 w-px bg-slate-300"></div>
            <span className="text-sm font-semibold text-purple-700" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Admin Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block">{user.name || user.phone}</span>
            <Button data-testid="admin-logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500 hover:text-slate-700">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 shadow-sm mb-6">
            <TabsTrigger data-testid="tab-overview" value="overview" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger data-testid="tab-venues" value="venues" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Building2 className="h-4 w-4 mr-1.5" /> Venues
            </TabsTrigger>
            <TabsTrigger data-testid="tab-template" value="template" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-1.5" /> Template
            </TabsTrigger>
            <TabsTrigger data-testid="tab-subadmins" value="subadmins" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-1.5" /> Sub-Admins
            </TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard icon={BarChart3} label="Total Venues" value={stats.total} color="text-slate-600" bg="bg-slate-50" />
              <StatCard icon={Clock} label="Draft" value={stats.draft} color="text-slate-500" bg="bg-slate-50" />
              <StatCard icon={Send} label="Submitted" value={stats.submitted} color="text-amber-600" bg="bg-amber-50" />
              <StatCard icon={CheckCircle2} label="Executed" value={stats.executed} color="text-green-600" bg="bg-green-50" />
            </div>

            <Card className="border-slate-200 mb-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Recent Venues</CardTitle>
              </CardHeader>
              <CardContent>
                <VenueTable venues={venues.slice(0, 5)} onDownload={handleDownloadVenuePDF} onDelete={handleDeleteVenue} />
              </CardContent>
            </Card>

            {user.role === "admin" && (
              <Card className="border-red-200 bg-red-50/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold text-red-800" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Danger Zone</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Delete All Venue & OTP Data</p>
                      <p className="text-xs text-slate-500 mt-1">This will permanently remove all venues, OTP records, and non-admin user accounts. Admin accounts and templates are preserved.</p>
                    </div>
                    <Button
                      data-testid="delete-all-data-btn"
                      variant="destructive"
                      onClick={() => setShowDeleteDialog(true)}
                      className="bg-red-600 hover:bg-red-700 text-white shrink-0 ml-4"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete All Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Venues */}
          <TabsContent value="venues">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>All Venues</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        data-testid="venue-search-input"
                        placeholder="Search venues..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64 bg-slate-50 border-slate-200"
                      />
                    </div>
                    <select
                      data-testid="venue-status-filter"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="submitted">Submitted</option>
                      <option value="executed">Executed</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {venuesLoading ? (
                  <div className="flex items-center justify-center py-12 text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
                  </div>
                ) : (
                  <VenueTable venues={venues} onDownload={handleDownloadVenuePDF} onDelete={handleDeleteVenue} />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Template */}
          <TabsContent value="template">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Agreement Template</CardTitle>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input type="file" accept=".txt,.docx" onChange={handleFileUpload} className="hidden" />
                      <div className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-slate-700">
                        <FileUp className="h-4 w-4" /> Upload File
                      </div>
                    </label>
                    <Button
                      data-testid="save-template-btn"
                      onClick={handleUpdateTemplate}
                      disabled={templateLoading}
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      {templateLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Save Template
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea
                  data-testid="template-textarea"
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  className="min-h-[60vh] bg-slate-50 border-slate-200 font-mono text-sm"
                  placeholder="Paste or type the agreement template here..."
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sub-Admins */}
          <TabsContent value="subadmins">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Sub-Admins</CardTitle>
                  {user.role === "admin" && (
                    <Button
                      data-testid="add-subadmin-btn"
                      onClick={() => setShowSubAdminDialog(true)}
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      <Plus className="h-4 w-4 mr-1.5" /> Add Sub-Admin
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {subAdmins.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No sub-admins yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subAdmins.map((sa) => (
                        <TableRow key={sa.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium text-slate-900">{sa.name}</TableCell>
                          <TableCell className="text-slate-600">{sa.phone}</TableCell>
                          <TableCell className="text-slate-600">{sa.email}</TableCell>
                          <TableCell className="text-right">
                            {user.role === "admin" && (
                              <Button
                                data-testid={`delete-subadmin-${sa.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSubAdmin(sa.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Sub-Admin Dialog */}
      <Dialog open={showSubAdminDialog} onOpenChange={setShowSubAdminDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Create Sub-Admin</DialogTitle>
            <DialogDescription>Add a new sub-admin who can access the admin portal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Name <span className="text-purple-600">*</span></Label>
              <Input
                data-testid="subadmin-name-input"
                value={newSubAdmin.name}
                onChange={(e) => setNewSubAdmin(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Phone <span className="text-purple-600">*</span></Label>
              <Input
                data-testid="subadmin-phone-input"
                value={newSubAdmin.phone}
                onChange={(e) => setNewSubAdmin(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                placeholder="10-digit mobile number"
                maxLength={10}
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">Email</Label>
              <Input
                data-testid="subadmin-email-input"
                type="email"
                value={newSubAdmin.email}
                onChange={(e) => setNewSubAdmin(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
                className="bg-slate-50 border-slate-200"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubAdminDialog(false)} className="border-slate-200">Cancel</Button>
            <Button
              data-testid="confirm-create-subadmin-btn"
              onClick={handleCreateSubAdmin}
              disabled={subAdminLoading}
              className="bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
            >
              {subAdminLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Data Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setDeleteConfirmText(""); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <AlertTriangle className="h-5 w-5" /> Delete All Data
            </DialogTitle>
            <DialogDescription>
              This action is irreversible. All venues, OTP records, and non-admin user accounts will be permanently deleted. Admin accounts and agreement templates will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-slate-700">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                data-testid="delete-confirm-input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="bg-slate-50 border-slate-200 font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteConfirmText(""); }} className="border-slate-200">Cancel</Button>
            <Button
              data-testid="confirm-delete-all-btn"
              onClick={handleDeleteAllData}
              disabled={deleteConfirmText !== "DELETE" || deleteLoading}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete All Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl ${bg} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function VenueTable({ venues, onDownload, onDelete }) {
  if (!venues || venues.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No venues found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Venue Name</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>GSTIN</TableHead>
            <TableHead>Signatory</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {venues.map((v) => (
            <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`venue-row-${v.id}`}>
              <TableCell className="font-medium text-slate-900">{v.venue_name || v.trade_name || v.legal_name || "—"}</TableCell>
              <TableCell className="text-slate-600">{v.phone}</TableCell>
              <TableCell className="text-slate-600 font-mono text-xs">{v.gstin || "—"}</TableCell>
              <TableCell className="text-slate-600">{v.signatory_name || "—"}</TableCell>
              <TableCell>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[v.status] || STATUS_COLORS.draft}`}>
                  {v.status}
                </span>
              </TableCell>
              <TableCell className="text-right flex items-center justify-end gap-1">
                {v.status === "executed" && (
                  <Button
                    data-testid={`download-agreement-${v.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => onDownload(v)}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  >
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                )}
                {onDelete && (
                  <Button
                    data-testid={`delete-venue-${v.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(v)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
